import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Flame, ArrowLeft, Thermometer, Wind, Calendar, Filter, Home, DollarSign, AlertTriangle, Lightbulb, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEOHead from '../components/SEOHead';

const tips = [
  {
    icon: <Filter className="w-8 h-8" />,
    title: 'Change Your Filters Regularly',
    description: 'Replace or clean your HVAC filters every 1-3 months. Dirty filters restrict airflow, forcing your system to work harder and increasing energy costs. A clean filter also improves indoor air quality by trapping dust, allergens, and pollutants.'
  },
  {
    icon: <Thermometer className="w-8 h-8" />,
    title: 'Use a Programmable Thermostat',
    description: 'Set your thermostat to adjust temperatures automatically when you are away or sleeping. Lowering the temperature by 7-10 degrees for 8 hours a day can save up to 10% on annual heating and cooling costs.'
  },
  {
    icon: <Calendar className="w-8 h-8" />,
    title: 'Schedule Annual Maintenance',
    description: 'Have your HVAC system professionally inspected and serviced at least once a year. Schedule heating maintenance in the fall and cooling maintenance in the spring to catch small problems before they become expensive repairs.'
  },
  {
    icon: <Wind className="w-8 h-8" />,
    title: 'Keep Vents and Registers Clear',
    description: 'Make sure furniture, curtains, and rugs are not blocking your supply and return vents. Blocked vents cause uneven heating or cooling and put extra strain on your system, reducing its efficiency and lifespan.'
  },
  {
    icon: <Home className="w-8 h-8" />,
    title: 'Seal Leaks and Insulate',
    description: 'Check around windows, doors, and ductwork for air leaks. Sealing gaps with caulk or weatherstripping and adding insulation to your attic and walls can dramatically reduce the workload on your HVAC system.'
  },
  {
    icon: <DollarSign className="w-8 h-8" />,
    title: 'Optimize for Energy Efficiency',
    description: 'Use ceiling fans to circulate air, close blinds during hot afternoons, and open them on sunny winter days for free solar heat. These small habits can reduce how often your HVAC system needs to run.'
  },
  {
    icon: <AlertTriangle className="w-8 h-8" />,
    title: 'Know the Warning Signs',
    description: 'Watch for unusual noises, strange odors, uneven temperatures, frequent cycling, or a sudden increase in energy bills. These can indicate your system needs professional attention. Addressing issues early prevents costly breakdowns.'
  },
  {
    icon: <Lightbulb className="w-8 h-8" />,
    title: 'Keep Your Outdoor Unit Clean',
    description: 'Clear leaves, debris, and vegetation from around your outdoor condenser unit. Maintain at least two feet of clearance on all sides. Rinse the unit gently with a garden hose seasonally to remove built-up dirt and improve performance.'
  }
];

const HvacTips = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="HVAC Tips for Homeowners | Heating & Cooling Maintenance Guide | Pierce County WA"
        description="Practical HVAC tips from licensed technicians in Pierce County, WA. Learn how to maintain your heating and cooling system, save energy, and avoid costly repairs. Serving Tacoma, Puyallup, Lakewood."
        keywords="HVAC tips Pierce County, heating maintenance tips Tacoma, AC maintenance guide Puyallup, HVAC energy saving tips, furnace maintenance homeowner, air conditioning tips WA, HVAC filter replacement, thermostat programming tips"
        canonicalPath="/tips"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "HVAC Tips for Homeowners in Pierce County, WA",
          "description": "Practical HVAC maintenance tips from licensed technicians serving Tacoma, Puyallup, and Pierce County.",
          "author": {
            "@type": "Organization",
            "name": "Taking Over Contracting Inc"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Taking Over Contracting Inc"
          }
        }}
      />
      {/* Header */}
      <header className="bg-black text-white py-4 px-6 fixed w-full top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Taking Over Contracting Inc</h1>
          </Link>
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/reviews" className="hover:text-red-500 transition-colors font-semibold flex items-center space-x-1">
              <Star className="w-4 h-4" />
              <span>Leave a Review</span>
            </Link>
            <a href="tel:2535017893" className="flex items-center space-x-2 hover:text-red-500 transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">(253) 501-7893</span>
            </a>
            <Button asChild className="bg-red-600 hover:bg-red-700 text-white">
              <Link to="/#contact">Get a Quote</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <Button asChild variant="ghost" className="text-gray-600 hover:text-red-600 -ml-4 mb-6">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
          <div className="text-center">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Homeowner Resources
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              HVAC Tips for Homeowners in Pierce County, WA
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Keep your heating and cooling system running efficiently with these practical tips from our licensed HVAC technicians serving Tacoma, Puyallup, and the greater Pierce County area.
            </p>
          </div>
        </div>
      </section>

      {/* Tips Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {tips.map((tip, index) => (
              <Card
                key={index}
                className="border-2 border-gray-100 hover:border-red-600 hover:shadow-xl transition-all duration-300"
              >
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-red-600 rounded-full flex items-center justify-center text-white">
                      {tip.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2">{tip.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{tip.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">Need Professional HVAC Help in Pierce County?</h3>
          <p className="text-xl text-gray-300 mb-8">
            Our certified technicians are ready to help with maintenance, repairs, and installations across Tacoma, Puyallup, Lakewood, and all of Pierce County, WA.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = 'tel:2535017893'}
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 rounded-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              (253) 501-7893
            </Button>
            <Button asChild className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6 rounded-lg">
              <Link to="/#contact">Request Service</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4 hover:opacity-90 transition-opacity">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Taking Over Contracting Inc</h3>
              </Link>
              <p className="text-gray-400">
                Your trusted licensed general contractor in Pierce County, WA. Professional HVAC, heating, cooling, and remodeling services in Tacoma, Puyallup, and surrounding areas.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-red-500 transition-colors">Home</Link></li>
                <li><Link to="/#services" className="hover:text-red-500 transition-colors">Services</Link></li>
                <li><Link to="/tips" className="hover:text-red-500 transition-colors">HVAC Tips</Link></li>
                <li><Link to="/reviews" className="hover:text-red-500 transition-colors">Reviews</Link></li>
                <li><Link to="/#contact" className="hover:text-red-500 transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:2535017893" className="hover:text-red-500 transition-colors">(253) 501-7893</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:josh.takingovercontracting@gmail.com" className="hover:text-red-500 transition-colors text-sm">
                    josh.takingovercontracting@gmail.com
                  </a>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 mt-1" />
                  <span className="text-sm">1723 13th Ave Ct NW<br />Puyallup, WA 98371</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">UBI# 60589170 &nbsp;|&nbsp; License# TAKINOC755K2</p>
            <p>&copy; {new Date().getFullYear()} Taking Over Contracting Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HvacTips;
